import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeFor } from "../context/AuthContext";
import { fmtErr } from "../api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, GitCompareArrows, FileCheck2 } from "lucide-react";

export function AuthLayout({ children, title, subtitle }) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-slate-950 p-12 text-white lg:flex">
        <Link to="/" data-testid="auth-home-link"><Logo boxed height="h-12" /></Link>
        <div>
          <h2 className="text-3xl font-extrabold leading-tight">AI-verified property transactions for Karnataka.</h2>
          <ul className="mt-8 space-y-4 text-sm text-slate-300">
            <li className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-blue-400" /> OCR extraction from Sale Deed, Khata, RTC & EC</li>
            <li className="flex items-center gap-3"><GitCompareArrows className="h-5 w-5 text-blue-400" /> Cross-document mismatch flags with evidence</li>
            <li className="flex items-center gap-3"><FileCheck2 className="h-5 w-5 text-blue-400" /> Advocate-grade Title Scrutiny Report, unlocked in minutes</li>
          </ul>
        </div>
        <p className="text-xs text-slate-500">© 2026 PropLoX · Bengaluru</p>
      </div>
      <div className="flex items-center justify-center bg-slate-50 px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-block lg:hidden"><Logo height="h-10" /></Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(homeFor(u));
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Log in to your PropLoX portal">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
          {loading ? "Logging in…" : "Log in"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          New to PropLoX?{" "}
          <Link to="/register" className="font-semibold text-blue-600 hover:underline" data-testid="login-register-link">Create an account</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
